// Simple i18n system for Dresby — German/English toggle

type Lang = 'de' | 'en';

const TRANSLATIONS: Record<string, Record<Lang, string>> = {
  // Nav
  'nav.home':    { de: 'Start',     en: 'Home' },
  'nav.about':   { de: 'Über uns',  en: 'About' },
  'nav.apps':    { de: 'Apps',      en: 'Apps' },
  'nav.projects':{ de: 'GitHub',    en: 'GitHub' },
  'nav.store':   { de: 'Shop',      en: 'Store' },
  'nav.blog':    { de: 'Devlog',    en: 'Devlog' },
  'nav.contact': { de: 'Kontakt',   en: 'Contact' },

  // Mode tabs
  'mode.browse': { de: 'Durchsuchen', en: 'Browse' },
  'mode.play':   { de: 'Spielen',     en: 'Play' },
  'mode.build':  { de: 'Bauen',       en: 'Build' },

  // Play hint
  'play.hint': { de: 'Klicke auf die leuchtenden Portale in der Welt zum Navigieren • Wechsle oben in den Spielmodus',
                 en: 'Click the glowing portals in the world to navigate • Switch to Play mode above' },

  // Terminal placeholder
  'terminal.placeholder': { de: "'help' für Befehle eingeben", en: "type 'help' to begin" },

  // Loading
  'loading.init': { de: 'Systeme werden initialisiert...', en: 'Initializing systems...' },
  'loading.check':{ de: 'Systeme werden geprüft...',      en: 'Checking systems...' },
  'loading.world':{ de: 'Welt wird geladen...',           en: 'Loading world...' },
  'loading.build':{ de: 'Szene wird aufgebaut...',        en: 'Building scene...' },
  'loading.routes':{ de: 'Routen werden initialisiert...',en: 'Initializing routes...' },
  'loading.ready':{ de: 'Fast fertig...',                 en: 'Almost ready...' },
  'loading.welcome':{ de: 'Willkommen bei Dresby.',       en: 'Welcome to Dresby.' },

  // Fallback
  'fallback.text': { de: 'Ihr Browser unterstützt kein WebGL. Die interaktive 3D-Erfahrung ist nicht verfügbar, aber alle Inhalte sind unten zugänglich.',
                     en: 'Your browser does not support WebGL. The interactive 3D experience is unavailable, but all content is accessible below.' },
  'fallback.skip': { de: 'Zum Hauptinhalt springen', en: 'Skip to main content' },
};

class I18n {
  private lang: Lang = 'de';
  private listeners: (() => void)[] = [];

  constructor() {
    // Check saved preference or browser language
    const saved = localStorage.getItem('dresby-lang') as Lang | null;
    if (saved === 'de' || saved === 'en') {
      this.lang = saved;
    } else if (navigator.language.startsWith('de')) {
      this.lang = 'de';
    } else {
      this.lang = 'en';
    }
  }

  get current(): Lang { return this.lang; }

  t(key: string): string {
    return TRANSLATIONS[key]?.[this.lang] ?? key;
  }

  toggle(): void {
    this.lang = this.lang === 'de' ? 'en' : 'de';
    localStorage.setItem('dresby-lang', this.lang);
    document.documentElement.lang = this.lang;
    this.listeners.forEach(fn => fn());
    this.updateDOM();
  }

  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  // Update static DOM elements with translations
  updateDOM(): void {
    // Nav links
    document.querySelectorAll('.nav-link').forEach(el => {
      const key = (el as HTMLElement).dataset.page;
      if (key) {
        const t = this.t(`nav.${key}`);
        if (t) el.textContent = t;
      }
    });

    // Mode tabs
    document.querySelectorAll('.mode-tab').forEach(el => {
      const mode = (el as HTMLElement).dataset.mode;
      if (mode) {
        const t = this.t(`mode.${mode}`);
        if (t) el.textContent = t;
      }
    });

    // Play hint
    const hint = document.querySelector('.play-hint span:last-child');
    if (hint) hint.textContent = this.t('play.hint');

    // Terminal placeholder
    const termInput = document.getElementById('terminal-input') as HTMLInputElement;
    if (termInput) termInput.placeholder = this.t('terminal.placeholder');

    // Skip link
    const skip = document.querySelector('.skip-link');
    if (skip) skip.textContent = this.t('fallback.skip');

    // Fallback text
    const fbText = document.querySelector('#webgl-fallback p');
    if (fbText) fbText.textContent = this.t('fallback.text');

    // Language flag
    const flag = document.getElementById('lang-flag');
    if (flag) flag.textContent = this.lang === 'de' ? '🇩🇪' : '🇬🇧';
  }
}

export const i18n = new I18n();
